import type {AgentSelection, ChatState, SessionState} from '@microsoft/agent-host-protocol'
import type {CustomAgentSelectionRequest, EstablishedAhpSessionView, FirstTurnHandoff} from './contracts'

const CUSTOM_AGENT_RESOLUTION_TIMEOUT_MS = 15_000
type Customization = NonNullable<SessionState['customizations']>[number]
type ContainerCustomization = Extract<Customization, {children?: unknown}>
type AgentCustomization = Extract<NonNullable<ContainerCustomization['children']>[number], {type: 'agent'}>

function selectableAgents(customizations: readonly Customization[] | undefined): AgentCustomization[] {
  if (!customizations) return []

  return customizations.flatMap(customization => {
    if (!('children' in customization)) return []
    const legacyEnabled = (customization as {enabled?: unknown}).enabled
    const enabled =
      'enablement' in customization
        ? customization.enablement?.[0]?.enabled !== false
        : typeof legacyEnabled !== 'boolean' || legacyEnabled
    if (!enabled) return []
    return (customization.children ?? []).filter(
      (child): child is AgentCustomization =>
        child.type === 'agent' && child.enabled !== false && child.disableUserInvocation !== true,
    )
  })
}

function advertisedAgents(customizations: readonly Customization[] | undefined): AgentCustomization[] {
  if (!customizations) return []
  return customizations.flatMap(customization =>
    'children' in customization
      ? (customization.children ?? []).filter((child): child is AgentCustomization => child.type === 'agent')
      : [],
  )
}

function agentNameFromUri(uri: string): string | undefined {
  const prefix = 'copilot-agent:/'
  if (!uri.startsWith(prefix)) return undefined
  const encodedName = uri.slice(prefix.length)
  if (!encodedName || encodedName.includes('/') || encodedName.includes('?') || encodedName.includes('#')) {
    return undefined
  }

  try {
    return decodeURIComponent(encodedName)
  } catch {
    return undefined
  }
}

export function findCustomAgentSelection(
  state: SessionState | undefined,
  request: CustomAgentSelectionRequest,
): AgentSelection | undefined {
  const agents = selectableAgents(state?.customizations)
  const slugMatch = agents.find(agent => agentNameFromUri(agent.uri) === request.slug)
  if (slugMatch) return {uri: slugMatch.uri}

  const expectedDisplayName = request.displayName || request.slug
  const displayNameMatches = agents.filter(agent => agent.name === expectedDisplayName)
  const [displayNameMatch] = displayNameMatches
  return displayNameMatches.length === 1 && displayNameMatch ? {uri: displayNameMatch.uri} : undefined
}

/**
 * Carries forward the custom agent of the most recent turn that ran with one.
 * The host treats the agent as a per-turn setting and falls back to its default
 * agent for a turn that omits one. The agent is kept only while the host still
 * lists it.
 */
export function selectPreviousTurnCustomAgent(
  state: SessionState | undefined,
  chat:
    | {
        turns: ReadonlyArray<Pick<ChatState['turns'][number], 'message'>>
        activeTurn?: Pick<NonNullable<ChatState['activeTurn']>, 'message'>
      }
    | undefined,
): AgentSelection | undefined {
  if (!chat) return undefined
  const messages = [chat.activeTurn?.message, ...[...chat.turns].reverse().map(turn => turn.message)]
  const uri = messages.find(message => message?.agent?.uri)?.agent?.uri
  if (!uri) return undefined
  return selectableAgents(state?.customizations).some(agent => agent.uri === uri) ? {uri} : undefined
}

export async function resolveCustomAgentSelection(
  view: EstablishedAhpSessionView,
  request: CustomAgentSelectionRequest,
  timeoutMs = CUSTOM_AGENT_RESOLUTION_TIMEOUT_MS,
): Promise<AgentSelection> {
  const current = findCustomAgentSelection(view.getSnapshot().sessionState, request)
  if (current) return current

  return new Promise<AgentSelection>((resolve, reject) => {
    let unsubscribe = () => {}
    const timeout = setTimeout(() => {
      unsubscribe()
      const agents = advertisedAgents(view.getSnapshot().sessionState?.customizations)
      const requestedName = request.displayName || request.slug
      if (agents.length === 0) {
        reject(
          new Error(
            `The AHP host did not advertise any custom agents for this session, so "${requestedName}" could not be selected.`,
          ),
        )
        return
      }
      reject(new Error(`The custom agent "${requestedName}" did not match the agents advertised by the AHP host.`))
    }, timeoutMs)
    const check = () => {
      const selection = findCustomAgentSelection(view.getSnapshot().sessionState, request)
      if (!selection) return
      clearTimeout(timeout)
      unsubscribe()
      resolve(selection)
    }

    unsubscribe = view.subscribe(check)
    check()
  })
}

export function createCustomAgentPromptPreparer(
  request: CustomAgentSelectionRequest,
): NonNullable<FirstTurnHandoff['preparePrompt']> {
  return async (session, currentPrompt) => ({
    ...currentPrompt,
    agent: await resolveCustomAgentSelection(session.view, request),
  })
}
